import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Copy, MessageSquareWarning, LifeBuoy, Reply, FolderPlus, Eye, Pencil, Send } from "lucide-react";
import { formatDate, statusTone } from "@/lib/format";

export default function Clients() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company_name: "", project_ids: [] });
  const [lastTemp, setLastTemp] = useState(null);
  const [respondFor, setRespondFor] = useState(null);
  const [respondText, setRespondText] = useState("");
  const [respondStatus, setRespondStatus] = useState("In Progress");
  const [clientSearch, setClientSearch] = useState("");
  const [concernFilter, setConcernFilter] = useState("");
  const [projectPrompt, setProjectPrompt] = useState(null);
  const [projectForm, setProjectForm] = useState({ name: "", status: "Planned", priority: "Medium", budget: 0, start_date: "", due_date: "" });
  const [viewClient, setViewClient] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [reinviteBusy, setReinviteBusy] = useState({});

  const load = async () => {
    const [c, p, con] = await Promise.all([
      api.get("/clients"),
      api.get("/projects"),
      api.get("/concerns").catch(() => ({ data: [] })),
    ]);
    setItems(c.data); setProjects(p.data); setConcerns(con.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/clients", form);
      setLastTemp({ email: data.client.email, pw: data.temp_password });
      const created = data.client;
      setForm({ name: "", email: "", company_name: "", project_ids: [] });
      setNewOpen(false); load();
      toast.success("Client created — share the temporary password shown below.");
      // Prompt: create a project for this client right away
      setProjectForm({ name: "", status: "Planned", priority: "Medium", budget: 0, start_date: "", due_date: "" });
      setProjectPrompt(created);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitProject = async (e) => {
    e.preventDefault();
    try {
      const { data: proj } = await api.post("/projects", {
        name: projectForm.name, client_name: projectPrompt.name,
        status: projectForm.status, priority: projectForm.priority,
        budget: Number(projectForm.budget), start_date: projectForm.start_date,
        due_date: projectForm.due_date, assigned_freelancer_ids: [],
      });
      // Link the project to the client
      const nextIds = [...(projectPrompt.project_ids || []), proj.id];
      await api.patch(`/clients/${projectPrompt.id}`, { project_ids: nextIds });
      toast.success("Project created — jumping in.");
      setProjectPrompt(null);
      nav(`/projects/${proj.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setConcernStatus = async (c, status) => {
    try { await api.patch(`/concerns/${c.id}`, { status }); load(); toast.success(`Marked ${status}`); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openRespond = (c) => {
    setRespondFor(c);
    setRespondText(c.response || "");
    setRespondStatus(c.status === "Open" ? "In Progress" : c.status);
  };

  const submitResponse = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/concerns/${respondFor.id}`, { response: respondText, status: respondStatus });
      toast.success("Response sent to client");
      setRespondFor(null); setRespondText(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openConcerns = concerns.filter((c) => c.status !== "Closed").length;

  const saveEditClient = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/clients/${editClient.id}`, {
        name: editClient.name, email: editClient.email, company_name: editClient.company_name,
        project_ids: editClient.project_ids || [],
      });
      toast.success("Client updated");
      setEditClient(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reinvite = async (c) => {
    setReinviteBusy((s) => ({ ...s, [c.id]: true }));
    try {
      const { data } = await api.post(`/clients/${c.id}/reinvite`);
      setLastTemp({ email: c.email, pw: data.temp_password });
      toast.success(data.email_sent ? "Re-invite emailed" : "Re-invite ready — share the new password below.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setReinviteBusy((s) => ({ ...s, [c.id]: false }));
  };

  return (
    <div>
      <PageHeader
        title="Clients & Support"
        description="Manage client access and respond to concerns raised through the portal."
        actions={<button data-testid="new-client-btn" onClick={() => setNewOpen(true)} className="btn-primary">+ Invite Client</button>}
      />
      <div className="p-8 space-y-6">
        <div className="flex flex-wrap gap-2 items-center" data-testid="clients-filters">
          <Input data-testid="clients-search" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Search client name or email…" className="h-9 rounded-lg border-black/15 max-w-sm" />
          <select data-testid="clients-status-filter" value={concernFilter} onChange={(e) => setConcernFilter(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
            <option value="">All ticket statuses</option>
            <option>Open</option><option>In Progress</option><option>Resolved</option><option>Closed</option>
          </select>
          {(clientSearch || concernFilter) && (
            <button data-testid="clients-filters-clear" onClick={() => { setClientSearch(""); setConcernFilter(""); }} className="text-xs underline underline-offset-4">Clear</button>
          )}
        </div>
        {lastTemp && (
          <div className="border-2 border-royal-700 bg-cream-2 rounded-2xl p-4" data-testid="client-temp-cred">
            <div className="label-caps">Share these credentials with the client</div>
            <div className="mt-2 flex flex-wrap gap-6 text-sm font-mono-tight">
              <span>URL: <code>{window.location.origin}/client/login</code></span>
              <span>Email: <code>{lastTemp.email}</code></span>
              <span>Password: <code>{lastTemp.pw}</code>
                <button onClick={() => { navigator.clipboard.writeText(lastTemp.pw); toast.success("Copied"); }} className="ml-1 align-middle border border-black/15 p-1 rounded"><Copy size={12} /></button>
              </span>
            </div>
          </div>
        )}

        <div className="bg-white border border-black/10 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 label-caps flex items-center gap-2 text-royal-800">
            <LifeBuoy size={12} /> Support Tickets
            <span className="ml-auto font-mono-tight normal-case tracking-normal text-xs text-neutral-500">
              {openConcerns} open · {concerns.length} total
            </span>
          </div>
          <table className="fms-table w-full" data-testid="concerns-table">
            <thead><tr><th>Client</th><th>Ticket</th><th>Portal ID</th><th>Project</th><th>Status</th><th>Raised</th><th></th></tr></thead>
            <tbody>
              {concerns.filter((c) => !concernFilter || c.status === concernFilter).map((c) => (
                <tr key={c.id} data-testid={`concern-row-${c.id}`}>
                  <td>{c.client_name}</td>
                  <td>
                    <div className="font-semibold">{c.subject}</div>
                    <div className="text-xs text-ink/60 max-w-md line-clamp-2">{c.body}</div>
                    {c.response && (
                      <div className="text-xs text-royal-700 max-w-md mt-1 border-l-2 border-royal-700/40 pl-2" data-testid={`concern-response-${c.id}`}>
                        <span className="label-caps mr-1 text-royal-700">Replied</span>{c.response}
                      </div>
                    )}
                  </td>
                  <td className="font-mono-tight text-[11px] text-neutral-500">{c.client_id?.slice(0, 8) || "—"}</td>
                  <td className="text-sm text-neutral-600">{projects.find((p) => p.id === c.project_id)?.name || "—"}</td>
                  <td>
                    <select data-testid={`concern-status-${c.id}`} value={c.status} onChange={(e) => setConcernStatus(c, e.target.value)} className={`px-2 py-0.5 text-xs font-mono-tight uppercase rounded border border-black/15 bg-white`}>
                      <option>Open</option><option>In Progress</option><option>Resolved</option><option>Closed</option>
                    </select>
                  </td>
                  <td className="font-mono-tight text-neutral-500 text-xs">{formatDate(c.created_at)}</td>
                  <td>
                    <button data-testid={`concern-respond-${c.id}`} onClick={() => openRespond(c)} className="text-xs underline underline-offset-4 inline-flex items-center gap-1 text-royal-700"><Reply size={12} /> {c.response ? "Edit reply" : "Respond"}</button>
                  </td>
                </tr>
              ))}
              {concerns.length === 0 && <tr><td colSpan={7} className="text-center text-neutral-500 py-8">No tickets yet — clients can raise concerns from their portal.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-black/10 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 label-caps">Client roster</div>
          <table className="fms-table w-full" data-testid="clients-table">
            <thead><tr><th>Name</th><th>Company</th><th>Email</th><th>Projects</th><th>Since</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {items.filter((c) => !clientSearch || (c.name || "").toLowerCase().includes(clientSearch.toLowerCase()) || (c.email || "").toLowerCase().includes(clientSearch.toLowerCase())).map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}>
                  <td>{c.name}</td>
                  <td className="text-sm text-neutral-600">{c.company_name || "—"}</td>
                  <td className="text-sm text-neutral-600">{c.email}</td>
                  <td className="text-xs">{(c.project_ids || []).map((pid) => projects.find((p) => p.id === pid)?.name || pid).join(", ") || "—"}</td>
                  <td className="font-mono-tight text-neutral-500">{formatDate(c.created_at)}</td>
                  <td className="text-right whitespace-nowrap space-x-3">
                    <button data-testid={`view-client-${c.id}`} onClick={() => setViewClient(c)} className="text-xs underline underline-offset-4 inline-flex items-center gap-1"><Eye size={12} /> View</button>
                    <button data-testid={`edit-client-${c.id}`} onClick={() => setEditClient({ ...c, project_ids: [...(c.project_ids || [])] })} className="text-xs underline underline-offset-4 inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                    <button data-testid={`reinvite-client-${c.id}`} disabled={reinviteBusy[c.id]} onClick={() => reinvite(c)} className="text-xs underline underline-offset-4 inline-flex items-center gap-1 text-royal-700 disabled:opacity-50"><Send size={12} /> {reinviteBusy[c.id] ? "Sending…" : "Re-invite"}</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="text-center text-neutral-500 py-8">No clients yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!viewClient} onOpenChange={(o) => !o && setViewClient(null)}>
        <DialogContent className="rounded-[1.25rem] max-w-lg" data-testid="view-client-dialog">
          {viewClient && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{viewClient.name}</DialogTitle><DialogDescription>{viewClient.company_name || "No company on file"} · {viewClient.email}</DialogDescription></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="label-caps">Email</div><div className="font-mono-tight">{viewClient.email}</div></div>
                  <div><div className="label-caps">Since</div><div className="font-mono-tight">{formatDate(viewClient.created_at)}</div></div>
                </div>
                <div>
                  <div className="label-caps mb-2">Linked projects</div>
                  <ul className="space-y-1" data-testid="view-client-projects">
                    {(viewClient.project_ids || []).map((pid) => {
                      const p = projects.find((x) => x.id === pid);
                      return (
                        <li key={pid} className="border border-black/10 rounded-lg px-3 py-2 flex justify-between">
                          <span>{p?.name || pid}</span>
                          {p && <span className={`px-2 py-0.5 text-xs font-mono-tight uppercase rounded ${statusTone(p.status)}`}>{p.status}</span>}
                        </li>
                      );
                    })}
                    {(!viewClient.project_ids || viewClient.project_ids.length === 0) && <li className="text-neutral-500">No projects linked yet.</li>}
                  </ul>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setViewClient(null)} className="text-sm underline underline-offset-4 text-ink/60">Close</button>
                  <button data-testid="view-goto-edit" onClick={() => { setEditClient({ ...viewClient, project_ids: [...(viewClient.project_ids || [])] }); setViewClient(null); }} className="btn-primary inline-flex items-center gap-1"><Pencil size={12} /> Edit client</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editClient} onOpenChange={(o) => !o && setEditClient(null)}>
        <DialogContent className="rounded-[1.25rem] max-w-md" data-testid="edit-client-dialog">
          {editClient && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Edit client</DialogTitle></DialogHeader>
              <form onSubmit={saveEditClient} className="space-y-3">
                <div><label className="label-caps block mb-1">Name</label><Input data-testid="edit-client-name" required value={editClient.name || ""} onChange={(e) => setEditClient({ ...editClient, name: e.target.value })} className="rounded-lg border-black/15" /></div>
                <div><label className="label-caps block mb-1">Email</label><Input data-testid="edit-client-email" required type="email" value={editClient.email || ""} onChange={(e) => setEditClient({ ...editClient, email: e.target.value })} className="rounded-lg border-black/15" /></div>
                <div><label className="label-caps block mb-1">Company</label><Input data-testid="edit-client-company" value={editClient.company_name || ""} onChange={(e) => setEditClient({ ...editClient, company_name: e.target.value })} className="rounded-lg border-black/15" /></div>
                <div>
                  <label className="label-caps block mb-1">Linked projects</label>
                  <div className="border border-black/15 rounded-lg p-2 max-h-40 overflow-auto space-y-1">
                    {projects.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={(editClient.project_ids || []).includes(p.id)}
                          onChange={(e) => {
                            const ids = e.target.checked ? [...(editClient.project_ids || []), p.id] : (editClient.project_ids || []).filter((x) => x !== p.id);
                            setEditClient({ ...editClient, project_ids: ids });
                          }} />
                        <span>{p.name}</span> <span className="text-xs text-neutral-500">· {p.client_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <DialogFooter><button data-testid="save-edit-client" type="submit" className="btn-primary">Save changes</button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-[1.25rem]" data-testid="new-client-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Invite Client</DialogTitle><DialogDescription>They'll get read-only access. You can spin up a project for them right after.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><label className="label-caps block mb-1">Name</label><Input data-testid="new-client-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div><label className="label-caps block mb-1">Email</label><Input data-testid="new-client-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div><label className="label-caps block mb-1">Company</label><Input data-testid="new-client-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div>
              <label className="label-caps block mb-1">Assign to existing projects <span className="text-neutral-400">(optional)</span></label>
              <div className="border border-black/15 rounded-lg p-2 max-h-40 overflow-auto space-y-1" data-testid="assign-projects-list">
                {projects.length === 0 && <div className="text-xs text-neutral-500 px-1 py-2">No projects yet — create one after saving.</div>}
                {projects.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid={`assign-project-${p.id}`} checked={form.project_ids.includes(p.id)} onChange={(e) => {
                      setForm((prev) => {
                        const ids = e.target.checked ? [...prev.project_ids, p.id] : prev.project_ids.filter((x) => x !== p.id);
                        return { ...prev, project_ids: ids };
                      });
                    }} /> <span>{p.name}</span> <span className="text-xs text-neutral-500">· {p.client_name}</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter><button data-testid="submit-client" type="submit" className="btn-primary">Create client</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!projectPrompt} onOpenChange={(o) => !o && setProjectPrompt(null)}>
        <DialogContent className="rounded-[1.25rem]" data-testid="new-client-project-prompt">
          {projectPrompt && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl inline-flex items-center gap-2 text-royal-800"><FolderPlus size={18} /> Start their first project?</DialogTitle>
                <DialogDescription>Kick off a project for <b>{projectPrompt.name}</b>. You can add files and work orders from inside the project.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submitProject} className="space-y-3">
                <div><label className="label-caps block mb-1">Project name</label>
                  <Input data-testid="prompt-project-name" required value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} className="rounded-lg border-black/15" placeholder={`${projectPrompt.name} — Launch`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Status</label>
                    <select value={projectForm.status} onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      {["Planned", "Active", "In Progress", "On Hold"].map((s) => <option key={s}>{s}</option>)}
                    </select></div>
                  <div><label className="label-caps block mb-1">Priority</label>
                    <select value={projectForm.priority} onChange={(e) => setProjectForm({ ...projectForm, priority: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      <option>Low</option><option>Medium</option><option>High</option>
                    </select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Start</label><Input type="date" value={projectForm.start_date} onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })} className="rounded-lg border-black/15" /></div>
                  <div><label className="label-caps block mb-1">Due</label><Input type="date" value={projectForm.due_date} onChange={(e) => setProjectForm({ ...projectForm, due_date: e.target.value })} className="rounded-lg border-black/15" /></div>
                </div>
                <div><label className="label-caps block mb-1">Budget (INR)</label><Input type="number" value={projectForm.budget} onChange={(e) => setProjectForm({ ...projectForm, budget: e.target.value })} className="rounded-lg border-black/15" /></div>
                <DialogFooter className="gap-2">
                  <button type="button" data-testid="prompt-skip" onClick={() => setProjectPrompt(null)} className="text-sm underline underline-offset-4 text-ink/60">Skip for now</button>
                  <button data-testid="prompt-submit-project" type="submit" className="btn-primary">Create project & open</button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!respondFor} onOpenChange={(o) => !o && setRespondFor(null)}>
        <DialogContent className="rounded-[1.25rem] max-w-lg" data-testid="respond-dialog">
          {respondFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-royal-800 flex items-center gap-2"><MessageSquareWarning size={18} /> Respond to ticket</DialogTitle>
                <DialogDescription>The client will see your response instantly in their portal.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="border border-black/10 rounded-lg p-3 bg-cream-2">
                  <div className="text-xs text-ink/60 font-mono-tight">{respondFor.client_name} · {formatDate(respondFor.created_at)}</div>
                  <div className="font-semibold mt-1">{respondFor.subject}</div>
                  <div className="text-sm text-ink/80 mt-1 whitespace-pre-wrap">{respondFor.body}</div>
                </div>
                <form onSubmit={submitResponse} className="space-y-3">
                  <div>
                    <label className="label-caps block mb-1">Your response</label>
                    <Textarea data-testid="respond-body" required rows={5} value={respondText} onChange={(e) => setRespondText(e.target.value)} className="rounded-lg border-black/15" />
                  </div>
                  <div>
                    <label className="label-caps block mb-1">Move status to</label>
                    <select data-testid="respond-status" value={respondStatus} onChange={(e) => setRespondStatus(e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                      <option>Open</option><option>In Progress</option><option>Resolved</option><option>Closed</option>
                    </select>
                  </div>
                  <DialogFooter>
                    <button data-testid="respond-submit" type="submit" className="btn-primary">Send response</button>
                  </DialogFooter>
                </form>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
